import { useEffect, useMemo, useState } from 'react';
import { format, subDays, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';

// ═══════════════════════════════════════════════════════════════════════════
// СЕТКА АКТИВНОСТИ «Твой путь» — дофаминовый якорь (heatmap-квадратики)
// ───────────────────────────────────────────────────────────────────────────
// Заполняется АВТОМАТИЧЕСКИ по данным (еда/активность/шаги). Тир-глоу:
//   0 — пусто (тускло, БЕЗ красного/стыда)
//   1 — «показался»  (≥1 приём еды ИЛИ активность ИЛИ шаги)
//   2 — «полный день» (калории ≥ 50% дневной цели — реальное ведение)
// Вехи считаются по НАКОПЛЕННЫМ активным дням (level ≥ 1), не по стрику подряд.
// Курс-триггер (дни 1–21) добавим слоем позже — база уже держит сетку.
// ═══════════════════════════════════════════════════════════════════════════

type Period = 30 | 90 | 180 | 360;
const PERIODS: Period[] = [30, 90, 180, 360];
const MILESTONES = [7, 21, 30, 66, 90, 180, 365];

interface DayCell {
  date: string; // yyyy-MM-dd
  calories: number;
  protein: number;
  hasActivity: boolean;
  steps: number;
  level: 0 | 1 | 2;
}

// Размер квадрата зависит от периода (чем длиннее — тем мельче клетки)
function squareSize(period: Period): number {
  if (period <= 30) return 22;
  if (period <= 90) return 15;
  if (period <= 180) return 11;
  return 8; // 360 — «год одним взглядом»
}

function levelStyle(level: 0 | 1 | 2): React.CSSProperties {
  if (level === 2) {
    return {
      background: '#10E58E',
      boxShadow: '0 0 6px rgba(16,229,142,0.7)',
    };
  }
  if (level === 1) return { background: 'rgba(16,229,142,0.34)' };
  return { background: 'rgba(255,255,255,0.05)' };
}

export function ConsistencyGrid() {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [period, setPeriod] = useState<Period>(90);
  const [cells, setCells] = useState<DayCell[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DayCell | null>(null);

  useEffect(() => {
    if (!profile?.id) return;
    load();
    setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, profile?.id]);

  async function load() {
    if (!profile?.id) return;
    setLoading(true);
    const since = subDays(startOfDay(new Date()), period - 1);
    const sinceIso = since.toISOString();
    const sinceDate = format(since, 'yyyy-MM-dd');
    const target = profile.daily_calories ?? 2000;

    const [mealsRes, actRes, stepsRes] = await Promise.all([
      supabase
        .from('meals')
        .select('consumed_at,total_calories,total_protein_g')
        .eq('user_id', profile.id)
        .gte('consumed_at', sinceIso),
      supabase
        .from('activity_log')
        .select('logged_at')
        .eq('user_id', profile.id)
        .gte('logged_at', sinceIso),
      supabase
        .from('steps_log')
        .select('logged_at,steps')
        .eq('user_id', profile.id)
        .gte('logged_at', sinceDate),
    ]);

    // Каркас дней (старые → новые)
    const map = new Map<string, DayCell>();
    for (let i = 0; i < period; i++) {
      const d = format(subDays(new Date(), period - 1 - i), 'yyyy-MM-dd');
      map.set(d, { date: d, calories: 0, protein: 0, hasActivity: false, steps: 0, level: 0 });
    }

    (mealsRes.data ?? []).forEach((m: any) => {
      const d = format(new Date(m.consumed_at), 'yyyy-MM-dd');
      const c = map.get(d);
      if (c) {
        c.calories += m.total_calories || 0;
        c.protein += Number(m.total_protein_g) || 0;
      }
    });
    (actRes.data ?? []).forEach((a: any) => {
      const d = format(new Date(a.logged_at), 'yyyy-MM-dd');
      const c = map.get(d);
      if (c) c.hasActivity = true;
    });
    (stepsRes.data ?? []).forEach((s: any) => {
      const d = format(new Date(s.logged_at), 'yyyy-MM-dd');
      const c = map.get(d);
      if (c) c.steps = Math.max(c.steps, s.steps || 0);
    });

    // Уровень глоу
    for (const c of map.values()) {
      const showedUp = c.calories > 0 || c.hasActivity || c.steps > 0;
      if (c.calories >= target * 0.5) c.level = 2;
      else if (showedUp) c.level = 1;
      else c.level = 0;
    }

    setCells(Array.from(map.values()));
    setLoading(false);
  }

  const activeDays = useMemo(() => cells.filter((c) => c.level >= 1).length, [cells]);
  const fullDays = useMemo(() => cells.filter((c) => c.level === 2).length, [cells]);
  const nextMilestone = useMemo(
    () => MILESTONES.find((m) => m > activeDays),
    [activeDays],
  );
  const lastReached = useMemo(
    () => [...MILESTONES].reverse().find((m) => m <= activeDays && activeDays > 0),
    [activeDays],
  );

  const size = squareSize(period);
  const todayCell = cells[cells.length - 1];
  const showedToday = todayCell && todayCell.level >= 1;

  return (
    <div className="card p-4 mb-5 border-emerald-glow/20">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-1">
        <span className="font-display font-semibold">Твой путь</span>
        <span className="text-[11px] text-ink-dim">{activeDays} активных дней</span>
      </div>
      <p className="text-xs text-ink-muted mb-3">
        {showedToday
          ? 'Сегодня квадрат горит — так держать 🔥'
          : 'Запиши приём еды — и квадрат сегодня загорится 🟢'}
      </p>

      {/* Переключатель периода */}
      <div className="flex gap-1 p-1 bg-bg-elevated rounded-xl mb-4">
        {PERIODS.map((p) => (
          <button
            key={p}
            onClick={() => { haptic('light'); setPeriod(p); }}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
              period === p ? 'bg-bg text-ink' : 'text-ink-dim'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Сетка */}
      {loading ? (
        <div className="h-24 flex items-center justify-center text-ink-dim text-xs">
          Загрузка…
        </div>
      ) : (
        <div
          className="flex flex-wrap content-start"
          style={{ gap: period <= 30 ? 6 : period <= 90 ? 4 : 3 }}
        >
          {cells.map((c) => {
            const isSel = selected?.date === c.date;
            return (
              <button
                key={c.date}
                onClick={() => { haptic('light'); setSelected(isSel ? null : c); }}
                title={c.date}
                style={{
                  width: size,
                  height: size,
                  borderRadius: Math.max(2, size / 5),
                  ...levelStyle(c.level),
                  outline: isSel ? '2px solid #3DD8FF' : 'none',
                  outlineOffset: 1,
                }}
                className="shrink-0 active:scale-90 transition-transform"
              />
            );
          })}
        </div>
      )}

      {/* Легенда */}
      <div className="flex items-center gap-1.5 mt-3 text-[10px] text-ink-dim">
        <span>меньше</span>
        <span style={{ width: 10, height: 10, borderRadius: 2, ...levelStyle(0) }} />
        <span style={{ width: 10, height: 10, borderRadius: 2, ...levelStyle(1) }} />
        <span style={{ width: 10, height: 10, borderRadius: 2, ...levelStyle(2) }} />
        <span>больше</span>
        <span className="ml-auto">🟩 полных дней: {fullDays}</span>
      </div>

      {/* Детали выбранного дня */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-line/40 text-xs">
              <div className="font-medium text-ink mb-1">
                {format(new Date(selected.date), 'd MMMM', { locale: ru })}
                {selected.level === 2 && <span className="text-emerald-glow"> · полный день</span>}
                {selected.level === 1 && <span className="text-emerald-glow/70"> · показался</span>}
                {selected.level === 0 && <span className="text-ink-dim"> · без записей</span>}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-ink-muted">
                <span>🍎 {Math.round(selected.calories)} ккал</span>
                {selected.protein > 0 && <span>белок {Math.round(selected.protein)} г</span>}
                {selected.hasActivity && <span>🏋 тренировка</span>}
                {selected.steps > 0 && <span>👟 {selected.steps.toLocaleString('ru')} шагов</span>}
                {selected.level === 0 && <span className="text-ink-dim">ничего не записано</span>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Вехи: маяк + взятая */}
      {!loading && (nextMilestone || lastReached) && (
        <div className="mt-3 pt-3 border-t border-line/40 space-y-1.5 text-xs">
          {nextMilestone && (
            <div className="flex items-center gap-1.5 text-emerald-glow">
              <span className="text-sm">🎯</span>
              <span>
                До вехи <strong>{nextMilestone}</strong> дней осталось{' '}
                <strong>{nextMilestone - activeDays}</strong>
                {nextMilestone === 66 && <span className="text-ink-dim"> · тут привычка выходит на автопилот</span>}
              </span>
            </div>
          )}
          {lastReached && (
            <div className="flex items-center gap-1.5 text-ink-dim">
              <span>🏅</span>
              <span>Взято: <strong className="text-ink-muted">{lastReached} дней</strong></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
